import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UniversalSearchBar } from './UniversalSearchBar';
import { theme } from '../config/theme';
import { BarChartIcon, CreditCardIcon, LogOutIcon, MenuIcon, XIcon, LayoutGridIcon, TerminalIcon, FileTextIcon, CalendarIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, ShoppingCartIcon, SettingsIcon, GlobeIcon, GraduationCapIcon, ChevronDownIcon, SmartphoneIcon, BanknoteIcon, MegaphoneIcon, HomeIcon, BedDoubleIcon, ClipboardListIcon, DollarSignIcon, BookOpenIcon, TruckIcon, FileIcon, AlertTriangleIcon, RefreshCwIcon, ShieldIcon, BarChart3Icon, HelpCircleIcon, BellIcon, PlusIcon, LinkIcon, RepeatIcon, MoreHorizontalIcon, ArrowUpDownIcon } from 'lucide-react';
const {
  colors,
  brand,
  layout
} = theme;
const {
  sidebar: sidebarColors,
  header: headerColors,
  assistant: assistantColors
} = colors;
interface AppLayoutProps {
  children: React.ReactNode;
}
export function AppLayout({
  children
}: AppLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [terminalsExpanded, setTerminalsExpanded] = useState(false);
  const [transactionsExpanded, setTransactionsExpanded] = useState(false);
  const [reportingExpanded, setReportingExpanded] = useState(false);
  const [balanceExpanded, setBalanceExpanded] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationTab, setNotificationTab] = useState<'unread' | 'all'>('unread');
  const notificationsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout
  } = useAuth();
  const placeholderTexts = ['How do I activate new payment methods?', 'How long does it take to receive a verdict on a dispute?', 'Can I refund a successful transaction from the past week?', "How long can I place a temporary hold on a customer's card for?"];
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholderTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const isActive = (path: string) => {
    if (path === '/overview') {
      return location.pathname === '/overview' || location.pathname === '/';
    }
    return location.pathname === path;
  };
  const isPaymentRequestsActive = () => {
    return location.pathname === '/payment-requests';
  };
  const isBalanceActive = () => {
    return ['/balance', '/balance-activity', '/payouts'].some(path => location.pathname.startsWith(path));
  };
  const isTransactionsActive = () => {
    return ['/sales', '/disputes', '/refunds', '/payment-analytics'].some(path => location.pathname.startsWith(path));
  };
  const isReportingActive = () => {
    return ['/reports', '/custom-metrics', '/nadi'].some(path => location.pathname.startsWith(path));
  };
  const getPageTitle = () => {
    if (location.pathname === '/overview' || location.pathname === '/') return 'Overview';
    if (location.pathname === '/payment-requests') return 'Payment Requests';
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname === '/virtual-terminal') return 'Virtual Terminal';
    if (location.pathname === '/physical-terminals') return 'Physical Terminals';
    if (location.pathname === '/softpos-terminals') return 'SoftPos Terminals';
    if (location.pathname === '/connect') return 'Connect OAuth2 Apps';
    if (location.pathname === '/orders') return 'Orders';
    if (location.pathname === '/tutorials') return 'Tutorials';
    if (location.pathname === '/campaigns') return 'Campaigns';
    if (location.pathname === '/campaign-details') return 'Campaign Details';
    if (location.pathname === '/campaign-form') return location.search.includes('id=') ? 'Update Campaign' : 'Create Campaign';
    if (location.pathname === '/invoices') return 'Invoices';
    if (location.pathname.startsWith('/invoice-detail/')) return 'Invoice Details';
    if (location.pathname === '/invoice-form') return location.search.includes('id=') ? 'Edit Invoice' : 'Create Invoice';
    if (location.pathname === '/customers') return 'Customers';
    if (location.pathname.startsWith('/customer-detail/')) return 'Customer Details';
    if (location.pathname === '/customer-form') return location.search.includes('id=') ? 'Edit Customer' : 'Create Customer';
    if (location.pathname === '/sales') return 'Sales';
    if (location.pathname.startsWith('/sale-detail/')) return 'Sale Details';
    if (location.pathname === '/disputes') return 'Disputes';
    if (location.pathname.startsWith('/dispute-detail/')) return 'Dispute Details';
    if (location.pathname === '/refunds') return 'Refunds';
    if (location.pathname.startsWith('/refund-detail/')) return 'Refund Details';
    if (location.pathname === '/suppliers') return 'Suppliers';
    if (location.pathname.startsWith('/supplier-detail/')) return 'Supplier Details';
    if (location.pathname === '/supplier-form') return location.search.includes('id=') ? 'Edit Supplier' : 'Create Supplier';
    if (location.pathname === '/supplier-bulk-upload') return 'Bulk Upload Suppliers';
    if (location.pathname === '/bills') return 'Bills';
    if (location.pathname.startsWith('/bill-detail/')) return 'Bill Details';
    if (location.pathname === '/bill-form') return location.search.includes('id=') ? 'Edit Bill' : 'Create Bill';
    if (location.pathname.startsWith('/bill-approval/')) return 'Bill Approval';
    if (location.pathname.startsWith('/bill-financing/')) return 'Request Bill Financing';
    if (location.pathname.startsWith('/tutorial/')) return 'Tutorial';
    if (location.pathname === '/reports') return 'Reports';
    if (location.pathname === '/custom-metrics') return 'Custom Metrics';
    if (location.pathname === '/nadi') return 'Nadi';
    return 'Dashboard';
  };
  // Nav item styles using theme values
  const navItemStyle = (active: boolean): React.CSSProperties => ({
    color: active ? sidebarColors.text.active : sidebarColors.text.default,
    fontWeight: active ? 500 : 400
  });
  const navItemHoverHandlers = (active: boolean) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!active) {
        e.currentTarget.style.color = sidebarColors.text.hover;
        e.currentTarget.style.backgroundColor = sidebarColors.hoverBg;
      }
    },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!active) {
        e.currentTarget.style.color = sidebarColors.text.default;
        e.currentTarget.style.backgroundColor = 'transparent';
      }
    }
  });
  const subNavItemStyle = (active: boolean): React.CSSProperties => ({
    color: active ? sidebarColors.text.active : sidebarColors.text.subDefault,
    fontWeight: active ? 500 : 400
  });
  const subNavHoverHandlers = (active: boolean) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!active) {
        e.currentTarget.style.color = sidebarColors.text.hover;
      }
    },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!active) {
        e.currentTarget.style.color = sidebarColors.text.subDefault;
      }
    }
  });
  // Close notifications on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const notifications = [{
    id: 1,
    message: 'Dispute #DP-2024-0001 requires your response. THB 100.01 fraudulent charge — deadline approaching.',
    date: 'Mar 15, 2026',
    unread: true,
    type: 'urgent'
  }, {
    id: 2,
    message: 'Dispute #DP-2024-0091 requires response. THB 1,538.94 duplicate charge — due in 5 hours.',
    date: 'Mar 15, 2026',
    unread: true,
    type: 'urgent'
  }, {
    id: 3,
    message: 'Bill from Acme Corp (INV-2026-003) is pending your approval. Due Mar 20, 2026.',
    date: 'Mar 14, 2026',
    unread: true,
    type: 'bill'
  }, {
    id: 4,
    message: 'Payment of THB 45,000 received from John Doe. Transaction completed successfully.',
    date: 'Mar 14, 2026',
    unread: true,
    type: 'payment'
  }, {
    id: 5,
    message: 'New refund processed for THB 2,500. Reason: Customer request. Refund ID #RF-0042.',
    date: 'Mar 13, 2026',
    unread: true,
    type: 'refund'
  }, {
    id: 6,
    message: 'Settlement completed successfully at 01:30 AM. All funds have been transferred.',
    date: 'Mar 13, 2026',
    unread: false,
    type: 'system'
  }, {
    id: 7,
    message: 'API uptime report: All systems operational. 99.99% uptime this month.',
    date: 'Mar 12, 2026',
    unread: false,
    type: 'system'
  }, {
    id: 8,
    message: 'New payment method integration available: Cryptocurrency payments now supported.',
    date: 'Mar 10, 2026',
    unread: false,
    type: 'system'
  }];
  const unreadNotifications = notifications.filter(n => n.unread);
  const displayNotifications = notificationTab === 'unread' ? unreadNotifications : notifications;
  return <div className="min-h-screen" style={{
    backgroundColor: colors.background.page
  }} data-id="element-1352">
      {/* Top Header Bar - spans FULL width */}
      <div className="fixed top-0 left-0 right-0 z-30" style={{
      backgroundColor: headerColors.bg,
      height: layout.header.height
    }} data-id="element-1353">
        <div className="flex items-center h-full" data-id="element-1354">
          {/* Logo section - exactly matches sidebar width */}
          <div className="hidden lg:flex items-center justify-center flex-shrink-0 h-full" style={{
          width: layout.sidebar.width,
          backgroundColor: sidebarColors.bg,
          borderBottom: `1px solid ${sidebarColors.bg}`
        }} data-id="element-1355">
            <img src="https://cdn.magicpatterns.com/uploads/vqr9LriSp5eEW29oygg9EF/rabbit_logo_sidebar.svg" alt="Rabbit Gateway" className="h-7 w-auto object-contain" data-id="element-1356" />
          </div>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center px-5 flex-shrink-0 h-full" style={{
          backgroundColor: sidebarColors.bg,
          borderBottom: `1px solid ${sidebarColors.bg}`
        }} data-id="element-1357">
            <img src="https://cdn.magicpatterns.com/uploads/vqr9LriSp5eEW29oygg9EF/rabbit_logo_sidebar.svg" alt="Rabbit Gateway" className="h-7 w-auto object-contain" data-id="element-1358" />
          </div>

          {/* Separator + hamburger + search + right icons */}
          <div className="flex items-center flex-1 h-full px-5 gap-5" style={{
          borderLeft: `1px solid ${headerColors.border}`,
          borderBottom: `1px solid ${headerColors.border}`
        }} data-id="element-1359">
            {/* Hamburger menu */}
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-1.5 flex-shrink-0 transition-colors" style={{
            color: colors.neutral[400]
          }} onMouseEnter={e => e.currentTarget.style.color = colors.neutral[600]} onMouseLeave={e => e.currentTarget.style.color = colors.neutral[400]} data-id="element-1360">
              <MenuIcon size={20} data-id="element-1361" />
            </button>

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl" data-id="element-1362">
              <UniversalSearchBar data-id="element-1363" />
            </div>

            {/* Right side */}
            <div className="flex items-center gap-1 ml-auto" data-id="element-1364">
              <button onClick={() => setAssistantOpen(!assistantOpen)} className="p-2 rounded-md hover:bg-gray-50 transition-colors relative group" data-id="element-1365">
                <HelpCircleIcon size={18} style={{
                color: headerColors.iconDefault
              }} data-id="element-1366" />
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1367">
                  Help
                </span>
              </button>

              <div className="relative" ref={notificationsRef} data-id="element-1368">
                <button onClick={() => setNotificationsOpen(!notificationsOpen)} className="p-2 rounded-md hover:bg-gray-50 transition-colors relative group" data-id="element-1369">
                  <BellIcon size={18} style={{
                  color: headerColors.iconDefault
                }} data-id="element-1370" />
                  {unreadNotifications.length > 0 && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full" style={{
                  backgroundColor: headerColors.notificationDot
                }} data-id="element-1371"></span>}
                  {!notificationsOpen && <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1372">
                      Notifications
                    </span>}
                </button>

                {notificationsOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setNotificationsOpen(false)} data-id="element-1373" />
                    <div className="absolute right-0 mt-2 w-[420px] bg-white rounded-xl shadow-xl border border-gray-200 z-20 overflow-hidden" data-id="element-1374">
                      {/* Header */}
                      <div className="flex items-center justify-between px-5 pt-4 pb-3" data-id="element-1375">
                        <h3 className="text-base font-semibold text-gray-900" data-id="element-1376">
                          Notifications
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-1377">
                          {unreadNotifications.length > 0 && <button className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-md transition-colors" data-id="element-1378">
                              Mark {unreadNotifications.length} as read
                            </button>}
                          <button onClick={() => {
                        setNotificationsOpen(false);
                        navigate('/settings');
                      }} className="text-xs font-medium text-gray-600 hover:text-gray-900 px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors" data-id="element-1379">
                            View preferences
                          </button>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="flex px-5 gap-2 mb-1" data-id="element-1380">
                        <button onClick={() => setNotificationTab('unread')} className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${notificationTab === 'unread' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1381">
                          Unread
                        </button>
                        <button onClick={() => setNotificationTab('all')} className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${notificationTab === 'all' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1382">
                          All
                        </button>
                      </div>

                      {/* Notification List */}
                      <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-100" data-id="element-1383">
                        {displayNotifications.length === 0 ? <div className="px-5 py-8 text-center" data-id="element-1384">
                            <p className="text-sm text-gray-500" data-id="element-1385">
                              No notifications
                            </p>
                          </div> : displayNotifications.slice(0, 5).map(notification => <div key={notification.id} className="px-5 py-3.5 hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-1386">
                                <div className="flex items-start gap-2" data-id="element-1387">
                                  {notification.unread && <span className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 flex-shrink-0" data-id="element-1388"></span>}
                                  <div className={`flex-1 min-w-0 ${!notification.unread ? 'ml-4' : ''}`} data-id="element-1389">
                                    <p className="text-sm text-gray-800 leading-relaxed" data-id="element-1390">
                                      {notification.message}
                                    </p>
                                    <div className="flex items-center justify-between mt-1.5" data-id="element-1391">
                                      <span className="text-xs text-gray-400" data-id="element-1392">
                                        {notification.date}
                                      </span>
                                      <button className="p-1 rounded hover:bg-gray-200 transition-colors" onClick={e => e.stopPropagation()} data-id="element-1393">
                                        <MoreHorizontalIcon size={14} className="text-gray-400" data-id="element-1394" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>)}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50" data-id="element-1395">
                        <span className="text-xs text-gray-500" data-id="element-1396">
                          {unreadNotifications.length} of {notifications.length}{' '}
                          unread notifications
                        </span>
                        <button onClick={() => {
                      setNotificationsOpen(false);
                      navigate('/notifications');
                    }} className="text-xs font-medium text-blue-600 hover:text-blue-700" data-id="element-1397">
                          View all
                        </button>
                      </div>
                    </div>
                  </>}
              </div>

              {/* Create Menu */}
              <div className="relative group" data-id="element-1398">
                <button onClick={() => setCreateMenuOpen(!createMenuOpen)} className="p-1.5 rounded-full transition-colors" style={{
                backgroundColor: headerColors.createButton
              }} onMouseEnter={e => e.currentTarget.style.backgroundColor = headerColors.createButtonHover} onMouseLeave={e => e.currentTarget.style.backgroundColor = headerColors.createButton} data-id="element-1399">
                  <PlusIcon size={16} className="text-white" data-id="element-1400" />
                </button>
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1401">
                  Create
                </span>
                {createMenuOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setCreateMenuOpen(false)} data-id="element-1402" />
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20" data-id="element-1403">
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/invoice-form');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1404">
                        <div className="flex items-center" data-id="element-1405">
                          <FileTextIcon size={18} className="mr-3 text-gray-500" data-id="element-1406" />
                          <span className="font-medium" data-id="element-1407">Invoice</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1408">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1409">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1410">
                            I
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=recurring');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1411">
                        <div className="flex items-center" data-id="element-1412">
                          <RepeatIcon size={18} className="mr-3 text-gray-500" data-id="element-1413" />
                          <span className="font-medium" data-id="element-1414">Subscription</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1415">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1416">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1417">
                            S
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=single');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1418">
                        <div className="flex items-center" data-id="element-1419">
                          <LinkIcon size={18} className="mr-3 text-gray-500" data-id="element-1420" />
                          <span className="font-medium" data-id="element-1421">Payment link</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1422">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1423">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1424">
                            L
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=charge');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1425">
                        <div className="flex items-center" data-id="element-1426">
                          <CreditCardIcon size={18} className="mr-3 text-gray-500" data-id="element-1427" />
                          <span className="font-medium" data-id="element-1428">Charge</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1429">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1430">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1431">
                            P
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=security-hold');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1432">
                        <div className="flex items-center" data-id="element-1433">
                          <ShieldIcon size={18} className="mr-3 text-gray-500" data-id="element-1434" />
                          <span className="font-medium" data-id="element-1435">Security hold</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1436">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1437">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1438">
                            O
                          </kbd>
                        </div>
                      </button>
                    </div>
                  </>}
              </div>

              {/* User Menu */}
              <div className="relative ml-2" data-id="element-1439">
                <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1440">
                  <img src="https://cdn.magicpatterns.com/uploads/dBrfzXiyML7jgesvo4qJJW/Screenshot_2026-03-17_at_06.40.16.png" alt="User profile" className="w-8 h-8 rounded-full object-cover" data-id="element-1441" />
                  <span className="ml-2 hidden sm:block text-sm font-medium text-gray-700" data-id="element-1442">
                    Simon
                  </span>
                  <ChevronDownIcon size={14} className={`ml-1 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} data-id="element-1443" />
                </button>
                {userMenuOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} data-id="element-1444" />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-1445">
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings?tab=company');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1446">
                        <UsersIcon size={16} className="mr-3 text-gray-500" data-id="element-1447" />
                        Account
                      </button>
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings?tab=company');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1448">
                        <BarChartIcon size={16} className="mr-3 text-gray-500" data-id="element-1449" />
                        Report
                      </button>
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1450">
                        <SettingsIcon size={16} className="mr-3 text-gray-500" data-id="element-1451" />
                        Settings
                      </button>
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings?tab=security');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1452">
                        <ShieldIcon size={16} className="mr-3 text-gray-500" data-id="element-1453" />
                        Security
                      </button>
                      <div className="border-t border-gray-100 my-1" data-id="element-1454" />
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                    navigate('/');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-1455">
                        <LogOutIcon size={16} className="mr-3" data-id="element-1456" />
                        Logout
                      </button>
                    </div>
                  </>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar - sits below header, aligned with logo column */}
      <div className={`
          fixed bottom-0 left-0 z-20 transform transition-transform duration-200 ease-in-out overflow-y-auto
          ${menuOpen ? 'translate-x-0 shadow-lg lg:shadow-none' : '-translate-x-full lg:translate-x-0'}
        `} style={{
      top: layout.header.height,
      width: layout.sidebar.width,
      backgroundColor: sidebarColors.bg,
      borderRight: `1px solid ${sidebarColors.border}`
    }} data-id="element-1457">
        <nav className="py-4 px-2" data-id="element-1458">
          {/* Dashboard */}
          <button onClick={() => navigate('/overview')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/overview'))} {...navItemHoverHandlers(isActive('/overview'))} data-id="element-1459">
            <LayoutGridIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1460" />
            Dashboard
          </button>

          {/* Section: Merchants & Partners */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-1461">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-1462">
              Merchants & Partners
            </span>
          </div>
          <button onClick={() => navigate('/payment-requests')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/payment-requests'))} {...navItemHoverHandlers(isActive('/payment-requests'))} data-id="element-1463">
            <CreditCardIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1464" />
            Payment requests
          </button>

          {/* Terminals */}
          <button onClick={() => setTerminalsExpanded(!terminalsExpanded)} className="w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals') || isActive('/connect'))} {...navItemHoverHandlers(isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals') || isActive('/connect'))} data-id="element-1465">
            <div className="flex items-center" data-id="element-1466">
              <TerminalIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1467" />
              Terminals
            </div>
            <ChevronDownIcon size={14} className={`transition-transform ${terminalsExpanded ? 'rotate-180' : ''}`} style={{
            color: sidebarColors.chevron
          }} data-id="element-1468" />
          </button>
          {terminalsExpanded && <div className="pl-7" data-id="element-1469">
              <button onClick={() => navigate('/virtual-terminal')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/virtual-terminal'))} {...subNavHoverHandlers(isActive('/virtual-terminal'))} data-id="element-1470">
                Virtual
              </button>
              <button onClick={() => navigate('/physical-terminals')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/physical-terminals'))} {...subNavHoverHandlers(isActive('/physical-terminals'))} data-id="element-1471">
                Physical
              </button>
              <button onClick={() => navigate('/softpos-terminals')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/softpos-terminals'))} {...subNavHoverHandlers(isActive('/softpos-terminals'))} data-id="element-1472">
                SoftPos
              </button>
              <button onClick={() => navigate('/connect')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/connect'))} {...subNavHoverHandlers(isActive('/connect'))} data-id="element-1473">
                Connect
              </button>
            </div>}

          <button onClick={() => navigate('/invoices')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/invoices'))} {...navItemHoverHandlers(isActive('/invoices'))} data-id="element-1474">
            <FileTextIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1475" />
            Invoices
          </button>
          <button onClick={() => navigate('/bills')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/bills') || location.pathname.startsWith('/bill-detail/') || isActive('/bill-form') || location.pathname.startsWith('/bill-approval/') || location.pathname.startsWith('/bill-financing/'))} {...navItemHoverHandlers(isActive('/bills') || location.pathname.startsWith('/bill-detail/') || isActive('/bill-form') || location.pathname.startsWith('/bill-approval/') || location.pathname.startsWith('/bill-financing/'))} data-id="element-1476">
            <FileIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1477" />
            Bills
          </button>

          {/* Relationships */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-1478">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-1479">
              Relationships
            </span>
          </div>
          <button onClick={() => navigate('/customers')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/customers'))} {...navItemHoverHandlers(isActive('/customers'))} data-id="element-1480">
            <UsersIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1481" />
            Customers
          </button>
          <button onClick={() => navigate('/suppliers')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/suppliers') || location.pathname.startsWith('/supplier-detail/') || isActive('/supplier-form'))} {...navItemHoverHandlers(isActive('/suppliers') || location.pathname.startsWith('/supplier-detail/') || isActive('/supplier-form'))} data-id="element-1482">
            <TruckIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1483" />
            Suppliers
          </button>

          {/* Payments */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-1484">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-1485">
              Payments
            </span>
          </div>
          <button onClick={() => setBalanceExpanded(!balanceExpanded)} className="w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isBalanceActive())} {...navItemHoverHandlers(isBalanceActive())} data-id="element-1486">
            <div className="flex items-center" data-id="element-1487">
              <BanknoteIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1488" />
              Balance
            </div>
            <ChevronDownIcon size={14} className={`transition-transform ${balanceExpanded ? 'rotate-180' : ''}`} style={{
            color: sidebarColors.chevron
          }} data-id="element-1489" />
          </button>
          {balanceExpanded && <div className="pl-7" data-id="element-1490">
              <button onClick={() => navigate('/balance')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/balance'))} {...subNavHoverHandlers(isActive('/balance'))} data-id="element-1491">
                Summary
              </button>
              <button onClick={() => navigate('/balance-activity')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/balance-activity'))} {...subNavHoverHandlers(isActive('/balance-activity'))} data-id="element-1492">
                Activity
              </button>
              <button onClick={() => navigate('/payouts')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/payouts'))} {...subNavHoverHandlers(isActive('/payouts'))} data-id="element-1493">
                Payouts
              </button>
            </div>}
          <button onClick={() => setTransactionsExpanded(!transactionsExpanded)} className="w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isTransactionsActive())} {...navItemHoverHandlers(isTransactionsActive())} data-id="element-1494">
            <div className="flex items-center" data-id="element-1495">
              <ArrowLeftRightIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1496" />
              Transactions
            </div>
            <ChevronDownIcon size={14} className={`transition-transform ${transactionsExpanded ? 'rotate-180' : ''}`} style={{
            color: sidebarColors.chevron
          }} data-id="element-1497" />
          </button>
          {transactionsExpanded && <div className="pl-7" data-id="element-1498">
              <button onClick={() => navigate('/payment-analytics')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/payment-analytics'))} {...subNavHoverHandlers(isActive('/payment-analytics'))} data-id="element-1499">
                Payment analytics
              </button>
              <button onClick={() => navigate('/sales')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/sales') || location.pathname.startsWith('/sale-detail/'))} {...subNavHoverHandlers(isActive('/sales') || location.pathname.startsWith('/sale-detail/'))} data-id="element-1500">
                Sales
              </button>
              <button onClick={() => navigate('/disputes')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/disputes') || location.pathname.startsWith('/dispute-detail/'))} {...subNavHoverHandlers(isActive('/disputes') || location.pathname.startsWith('/dispute-detail/'))} data-id="element-1501">
                Disputes
              </button>
              <button onClick={() => navigate('/refunds')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/refunds') || location.pathname.startsWith('/refund-detail/'))} {...subNavHoverHandlers(isActive('/refunds') || location.pathname.startsWith('/refund-detail/'))} data-id="element-1502">
                Refunds
              </button>
            </div>}

          {/* E-Commerce */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-1503">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-1504">
              E-Commerce
            </span>
          </div>
          <button onClick={() => navigate('/shops')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/shops'))} {...navItemHoverHandlers(isActive('/shops'))} data-id="element-1505">
            <ShoppingBagIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1506" />
            Shops
          </button>
          <button onClick={() => navigate('/orders')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/orders'))} {...navItemHoverHandlers(isActive('/orders'))} data-id="element-1507">
            <ShoppingCartIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1508" />
            Orders
          </button>
          <button onClick={() => navigate('/campaigns')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/campaigns') || isActive('/campaign-details') || isActive('/campaign-form'))} {...navItemHoverHandlers(isActive('/campaigns') || isActive('/campaign-details') || isActive('/campaign-form'))} data-id="element-1509">
            <MegaphoneIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1510" />
            Campaigns
          </button>

          {/* Reports & Analytics */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-1511">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-1512">
              Reports & Analytics
            </span>
          </div>
          <button onClick={() => setReportingExpanded(!reportingExpanded)} className="w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isReportingActive())} {...navItemHoverHandlers(isReportingActive())} data-id="element-1513">
            <div className="flex items-center" data-id="element-1514">
              <BarChartIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1515" />
              Reporting
            </div>
            <ChevronDownIcon size={14} className={`transition-transform ${reportingExpanded ? 'rotate-180' : ''}`} style={{
            color: sidebarColors.chevron
          }} data-id="element-1516" />
          </button>
          {reportingExpanded && <div className="pl-7" data-id="element-1517">
              <button onClick={() => navigate('/reports')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/reports'))} {...subNavHoverHandlers(isActive('/reports'))} data-id="element-1518">
                Reports
              </button>
              <button onClick={() => navigate('/custom-metrics')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/custom-metrics'))} {...subNavHoverHandlers(isActive('/custom-metrics'))} data-id="element-1519">
                Custom metrics
              </button>
              <button onClick={() => navigate('/nadi')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/nadi'))} {...subNavHoverHandlers(isActive('/nadi'))} data-id="element-1520">
                Nadi
              </button>
            </div>}

          {/* System */}
          <div className="mt-4 mb-1 px-4" data-id="element-1521">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-1522">
              System
            </span>
          </div>
          <button onClick={() => navigate('/settings')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/settings'))} {...navItemHoverHandlers(isActive('/settings'))} data-id="element-1523">
            <SettingsIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1524" />
            Settings
          </button>
          <button onClick={() => navigate('/tutorials')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/tutorials'))} {...navItemHoverHandlers(isActive('/tutorials'))} data-id="element-1525">
            <GraduationCapIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1526" />
            Tutorials
          </button>
          <div className="h-6" data-id="element-1527" />
        </nav>
      </div>

      {/* Main content */}
      <div className="lg:pl-60 min-h-screen" style={{
      paddingTop: layout.header.height
    }} data-id="element-1528">
        <div className="px-6 lg:px-8 py-6" data-id="element-1529">{children}</div>
      </div>

      {/* AI Assistant Panel */}
      {assistantOpen && <>
          <div className="fixed inset-0 z-40 transition-opacity" style={{
        backgroundColor: colors.background.overlay
      }} onClick={() => setAssistantOpen(false)} data-id="element-1530" />
          <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col" data-id="element-1531">
            <div className="flex items-center justify-between p-4" style={{
          borderBottom: `1px solid ${headerColors.border}`
        }} data-id="element-1532">
              <div className="flex items-center gap-2" data-id="element-1533">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              backgroundColor: brand.primary
            }} data-id="element-1534">
                  <span className="text-white text-sm font-semibold" data-id="element-1535">✨</span>
                </div>
                <h2 className="text-lg font-semibold" style={{
              color: colors.text.primary
            }} data-id="element-1536">
                  Assistant
                </h2>
              </div>
              <button onClick={() => setAssistantOpen(false)} className="p-1 rounded transition-colors hover:bg-gray-100" data-id="element-1537">
                <XIcon size={20} style={{
              color: headerColors.iconDefault
            }} data-id="element-1538" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-1539">
              <div className="space-y-4" data-id="element-1540">
                <div className="bg-gray-50 rounded-lg p-4" data-id="element-1541">
                  <p className="text-sm text-gray-700 mb-2" data-id="element-1542">
                    Hi, I'm Rabbit Gateway Assistant. 👋
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1543">
                    What do you need help with? Select a topic or type your
                    question below.
                  </p>
                </div>
                <div className="space-y-2" data-id="element-1544">
                  {['Contact merchant support', 'My verification status', 'Change business type', 'My settlement is missing', 'Update bank account for settlement', 'Malaysia payment regulations'].map(label => <button key={label} className="w-full text-left px-4 py-3 rounded-lg transition-colors" style={{
                border: `1px solid ${headerColors.border}`
              }} onMouseEnter={e => {
                e.currentTarget.style.borderColor = assistantColors.optionBorderHover;
                e.currentTarget.style.backgroundColor = assistantColors.optionBgHover;
              }} onMouseLeave={e => {
                e.currentTarget.style.borderColor = headerColors.border;
                e.currentTarget.style.backgroundColor = 'transparent';
              }} data-id="element-1545">
                      <p className="text-sm font-medium" style={{
                  color: colors.text.primary
                }} data-id="element-1546">
                        {label}
                      </p>
                    </button>)}
                </div>
                <div className="pt-4" style={{
              borderTop: `1px solid ${headerColors.border}`
            }} data-id="element-1547">
                  <p className="text-xs font-medium mb-2" style={{
                color: colors.text.tertiary
              }} data-id="element-1548">
                    RECENT
                  </p>
                  <button className="w-full text-left px-4 py-3 rounded-lg transition-colors" style={{
                border: `1px solid ${headerColors.border}`
              }} onMouseEnter={e => {
                e.currentTarget.style.borderColor = assistantColors.optionBorderHover;
                e.currentTarget.style.backgroundColor = assistantColors.optionBgHover;
              }} onMouseLeave={e => {
                e.currentTarget.style.borderColor = headerColors.border;
                e.currentTarget.style.backgroundColor = 'transparent';
              }} data-id="element-1549">
                    <p className="text-sm" style={{
                  color: colors.neutral[700]
                }} data-id="element-1550">
                      How do I enable FPX for my customers?
                    </p>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4" style={{
          borderTop: `1px solid ${headerColors.border}`
        }} data-id="element-1551">
              <div className="relative" data-id="element-1552">
                <input type="text" placeholder={placeholderTexts[placeholderIndex]} className="w-full px-4 py-3 pr-10 rounded-lg outline-none transition-all" style={{
              border: `1px solid ${colors.border.DEFAULT}`
            }} onFocus={e => {
              e.target.style.borderColor = assistantColors.inputFocusRing;
              e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
            }} onBlur={e => {
              e.target.style.borderColor = colors.border.DEFAULT;
              e.target.style.boxShadow = 'none';
            }} key={placeholderIndex} data-id="element-1553" />
                <button className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 transition-colors" style={{
              color: colors.neutral[400]
            }} onMouseEnter={e => e.currentTarget.style.color = colors.neutral[600]} onMouseLeave={e => e.currentTarget.style.color = colors.neutral[400]} data-id="element-1554">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" data-id="element-1555">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" data-id="element-1556" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </>}

      {/* Overlay for mobile */}
      {menuOpen && <div className="fixed inset-0 z-10 lg:hidden" style={{
      backgroundColor: colors.background.overlay
    }} onClick={() => setMenuOpen(false)} data-id="element-1557"></div>}
    </div>;
}